<script setup lang="ts">
import { computed } from 'vue'
import type { AcceptableValue } from 'reka-ui'
import type { InputChoiceSet } from '../types'
import { CheckIcon } from 'lucide-vue-next'
import { Label } from '@/components/ui/label'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxViewport,
} from '@/components/ui/combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { renderMarkdown } from '@/lib/markdown'
import { sanitizeRichHtml } from '@/lib/htmlSanitizer'
import { useCardContext } from '../composables/useCardContext'
import { useExclusiveOpenGroup } from '../composables/useExclusiveOpenGroup'
import { spacingClasses } from '../utils/classes'

const props = defineProps<{ element: InputChoiceSet }>()

const { inputs, errors, cardInstanceId, isEnabled } = useCardContext()
const selectOpenGroup = useExclusiveOpenGroup(`ac-choice-set:${cardInstanceId}:${props.element.id}:select`)
const filteredOpenGroup = useExclusiveOpenGroup(`ac-choice-set:${cardInstanceId}:${props.element.id}:filtered`)

const isExpanded = computed(() => props.element.style === 'expanded')
const isFiltered = computed(() => props.element.style === 'filtered' && !isMultiSelect.value)
const isMultiSelect = computed(() => props.element.isMultiSelect === true)
const inputReadOnly = computed(() => !isEnabled.value || props.element.isEnabled === false)
const firstChoiceValue = computed(() => props.element.choices[0]?.value ?? '')
const firstChoiceInputId = computed(() => buildOptionInputId(firstChoiceValue.value))
const labelForId = computed(() => {
  if (isMultiSelect.value || isExpanded.value) {
    return firstChoiceInputId.value
  }
  return fieldInputId.value
})
const fieldInputId = computed(() => buildFieldInputId(props.element.id))

function normalizeDomToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_')
}

function buildFieldInputId(elementId: string): string {
  return `${cardInstanceId}-${normalizeDomToken(elementId)}`
}

function buildOptionInputId(choiceValue: string): string {
  return `${fieldInputId.value}-${normalizeDomToken(choiceValue)}`
}

// For multi-select, value is comma-separated
const selectedValues = computed(() => {
  const val = inputs[props.element.id] ?? props.element.value ?? ''
  return val ? val.split(',') : []
})

function handleSelectChange(value: AcceptableValue) {
  inputs[props.element.id] = String(value ?? '')
  selectOpenGroup.close()
  clearError()
}

function handleRadioChange(value: AcceptableValue) {
  if (typeof value !== 'string') return
  inputs[props.element.id] = value
  clearError()
}

function handleCheckboxChange(choiceValue: string, checked: boolean) {
  const current = new Set(selectedValues.value)
  if (checked) {
    current.add(choiceValue)
  } else {
    current.delete(choiceValue)
  }
  inputs[props.element.id] = Array.from(current).join(',')
  clearError()
}

function isChecked(choiceValue: string): boolean {
  return selectedValues.value.includes(choiceValue)
}

function clearError() {
  if (errors[props.element.id]) {
    errors[props.element.id] = ''
  }
}

function displayChoiceTitle(value: AcceptableValue) {
  if (typeof value !== 'string') return ''
  return props.element.choices.find((choice) => choice.value === value)?.title ?? ''
}

function handleFilteredValueChange(value: AcceptableValue) {
  if (typeof value !== 'string') {
    inputs[props.element.id] = ''
    clearError()
    return
  }

  inputs[props.element.id] = value
  filteredOpenGroup.close()
  clearError()
}

function handleSelectOpenChange(nextOpen: boolean) {
  selectOpenGroup.setOpen(nextOpen)
}

function handleFilteredOpenChange(nextOpen: boolean) {
  filteredOpenGroup.setOpen(nextOpen)
}

function renderChoiceTitleHtml(title: string): string {
  return sanitizeRichHtml(renderMarkdown(title).html)
}
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5', spacingClasses[props.element.spacing ?? 'default'])">
    <Label v-if="props.element.label" :for="labelForId">
      {{ props.element.label }}
      <span v-if="props.element.isRequired" class="text-destructive">*</span>
    </Label>

    <!-- Multi-select: Checkboxes -->
    <div v-if="isMultiSelect" class="flex flex-col gap-2">
      <div
        v-for="choice in props.element.choices"
        :key="choice.value"
        class="flex items-center gap-2"
      >
        <Checkbox
          :id="buildOptionInputId(choice.value)"
          :disabled="inputReadOnly"
          :checked="isChecked(choice.value)"
          @update:checked="(checked: boolean) => handleCheckboxChange(choice.value, checked)"
        />
        <Label
          :for="buildOptionInputId(choice.value)"
          class="font-normal cursor-pointer"
        >
          <span v-html="renderChoiceTitleHtml(choice.title)" />
        </Label>
      </div>
    </div>

    <!-- Expanded single-select: Radio buttons -->
    <RadioGroup
      v-else-if="isExpanded"
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      @update:model-value="handleRadioChange"
    >
      <div
        v-for="choice in props.element.choices"
        :key="choice.value"
        class="flex items-center gap-2"
      >
        <RadioGroupItem
          :id="buildOptionInputId(choice.value)"
          :value="choice.value"
          :disabled="inputReadOnly"
        />
        <Label
          :for="buildOptionInputId(choice.value)"
          class="font-normal cursor-pointer"
        >
          <span v-html="renderChoiceTitleHtml(choice.title)" />
        </Label>
      </div>
    </RadioGroup>

    <!-- Filtered single-select: Combobox -->
    <Combobox
      v-else-if="isFiltered"
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      :disabled="inputReadOnly"
      :open="filteredOpenGroup.isOpen.value"
      @update:model-value="handleFilteredValueChange"
      @update:open="handleFilteredOpenChange"
    >
      <ComboboxAnchor as-child class="w-full">
        <ComboboxInput
          :id="fieldInputId"
          :display-value="displayChoiceTitle"
          :placeholder="props.element.placeholder ?? 'Type to filter options'"
          :disabled="inputReadOnly"
          :class="cn(
            'border-input h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-xs',
            errors[props.element.id] && 'border-destructive'
          )"
        />
      </ComboboxAnchor>
      <ComboboxList :class="cn('w-full p-0')">
        <ComboboxEmpty class="py-2 text-sm">
          No matching options
        </ComboboxEmpty>
        <ComboboxViewport class="max-h-56 p-1">
          <ComboboxGroup>
            <ComboboxItem
              v-for="choice in props.element.choices"
              :key="choice.value"
              :value="choice.value"
            >
              <span v-html="renderChoiceTitleHtml(choice.title)" />
              <ComboboxItemIndicator>
                <CheckIcon class="size-4" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxViewport>
      </ComboboxList>
    </Combobox>

    <!-- Compact (default): Dropdown -->
    <Select
      v-else
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      :disabled="inputReadOnly"
      :open="selectOpenGroup.isOpen.value"
      @update:model-value="handleSelectChange"
      @update:open="handleSelectOpenChange"
    >
      <SelectTrigger
        :id="fieldInputId"
        :class="cn('bg-background', errors[props.element.id] && 'border-destructive')"
      >
        <SelectValue :placeholder="props.element.placeholder ?? 'Select an option'" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          v-for="choice in props.element.choices"
          :key="choice.value"
          :value="choice.value"
        >
          <SelectItemText>
            <span v-html="renderChoiceTitleHtml(choice.title)" />
          </SelectItemText>
        </SelectItem>
      </SelectContent>
    </Select>

    <p v-if="errors[props.element.id]" class="text-sm text-destructive">
      {{ errors[props.element.id] }}
    </p>
  </div>
</template>
